/**
 * Visitor-session persistence for the embeddable widget.
 *
 * The chat backend assigns a per-conversation bearer token on the
 * FIRST `start` SSE event of a freshly created conversation (see
 * `apps/api/src/respondhero_api/services/chat_stream.py` and
 * `routers/widget.py`). We stash that token in localStorage so:
 *
 *   - A reload of the same page resumes the conversation. The widget
 *     reads back the token + conversation_id on mount and immediately
 *     polls for any messages that landed while the tab was closed
 *     (e.g. an operator email reply the visitor never saw).
 *   - A visitor returning within 24 hours sees their thread continued
 *     instead of starting from zero.
 *
 * Storage shape and rules:
 *
 *   - Key is namespaced per tenant slug:
 *     `respondhero:widget:{tenantSlug}:session`. Two RespondHero
 *     tenants on the same browser do not see each other's tokens.
 *   - Value is JSON with `conversationId`, `visitorSessionToken`,
 *     `lastSeenMessageId`, `lastSeenMessageAt`, and a `createdAt`
 *     timestamp used to expire sessions after 24 hours. Older
 *     entries are cleared on read.
 *   - Every localStorage call sits inside a try/catch. Safari private
 *     mode throws on `setItem`, some embedded webviews disable
 *     storage entirely. The widget MUST keep working without
 *     persistence; we return null / no-op rather than surface an
 *     error to the chat surface.
 *
 * CLAUDE.md sectie 3 (Hallon): the token is a per-conversation
 * bearer, not a credential. A leaked entry compromises ONE
 * conversation, not the tenant key. The backend compares the
 * `X-Visitor-Session` header byte-for-byte against the column on
 * `conversations`; cross-conversation reads fail 403 by construction.
 */

const STORAGE_NAMESPACE = "respondhero:widget";
const STORAGE_VERSION = 1;
// Twenty-four hours. A returning visitor within a day sees their
// thread resume; beyond that the conversation context is usually
// stale enough that starting fresh is the better visitor experience.
const MAX_SESSION_AGE_MS = 24 * 60 * 60 * 1000;

export interface VisitorSession {
  conversationId: string;
  visitorSessionToken: string;
  /** Most recent rendered message id; used by the widget for dedup. */
  lastSeenMessageId: string | null;
  /**
   * ISO-8601 timestamp of the most recent rendered message. Drives
   * the `?after=` query param on the next polling fetch so the
   * server returns only what is strictly newer.
   */
  lastSeenMessageAt: string | null;
}

interface StoredSession extends VisitorSession {
  version: number;
  createdAt: string;
}

function storageKey(tenantSlug: string): string {
  return `${STORAGE_NAMESPACE}:${tenantSlug}:session`;
}

function readStorage(): Storage | null {
  // `typeof localStorage` can throw in some sandboxed iframes before
  // the property access even returns; the surrounding try keeps the
  // probe safe even when the global exists but throws on access.
  try {
    if (typeof window === "undefined") return null;
    const storage = window.localStorage;
    // A test write+remove proves the API works AND is not blocked
    // (Safari private mode reports localStorage but throws on write).
    const probeKey = `${STORAGE_NAMESPACE}:probe`;
    storage.setItem(probeKey, "1");
    storage.removeItem(probeKey);
    return storage;
  } catch {
    return null;
  }
}

/**
 * Load the stored session for one tenant, or null if absent/expired.
 *
 * Returns null and clears the stored entry when:
 *   - storage is unavailable (incognito, sandboxed iframe)
 *   - the JSON cannot be parsed (corrupted entry)
 *   - the version field does not match (a future schema bump)
 *   - the entry is older than `MAX_SESSION_AGE_MS`
 */
export function loadVisitorSession(tenantSlug: string): VisitorSession | null {
  const storage = readStorage();
  if (!storage) return null;
  const key = storageKey(tenantSlug);
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: StoredSession;
  try {
    parsed = JSON.parse(raw) as StoredSession;
  } catch {
    // Corrupted entry: drop it so a future write succeeds clean.
    try {
      storage.removeItem(key);
    } catch {
      /* swallow */
    }
    return null;
  }
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    parsed.version !== STORAGE_VERSION ||
    typeof parsed.conversationId !== "string" ||
    typeof parsed.visitorSessionToken !== "string" ||
    typeof parsed.createdAt !== "string"
  ) {
    try {
      storage.removeItem(key);
    } catch {
      /* swallow */
    }
    return null;
  }
  // Expiry check. Anchor age to the stored `createdAt` rather than
  // last-seen so a long-running conversation does not survive
  // indefinitely just because each message renews the timer.
  const created = Date.parse(parsed.createdAt);
  if (Number.isNaN(created) || Date.now() - created > MAX_SESSION_AGE_MS) {
    try {
      storage.removeItem(key);
    } catch {
      /* swallow */
    }
    return null;
  }
  return {
    conversationId: parsed.conversationId,
    visitorSessionToken: parsed.visitorSessionToken,
    lastSeenMessageId:
      typeof parsed.lastSeenMessageId === "string" ? parsed.lastSeenMessageId : null,
    lastSeenMessageAt:
      typeof parsed.lastSeenMessageAt === "string" ? parsed.lastSeenMessageAt : null,
  };
}

/**
 * Persist (or overwrite) the session entry for one tenant.
 *
 * Failures (storage unavailable, quota exceeded) are swallowed: the
 * widget keeps working in memory and the visitor simply does not
 * resume on the next pageload.
 */
export function saveVisitorSession(tenantSlug: string, session: VisitorSession): void {
  const storage = readStorage();
  if (!storage) return;
  const payload: StoredSession = {
    ...session,
    version: STORAGE_VERSION,
    createdAt: new Date().toISOString(),
  };
  try {
    storage.setItem(storageKey(tenantSlug), JSON.stringify(payload));
  } catch {
    /* swallow */
  }
}

/**
 * Remove the stored session for one tenant.
 *
 * Called when the polling endpoint returns 403 (token mismatch or
 * legacy row) and when the visitor explicitly resets the chat.
 */
export function clearVisitorSession(tenantSlug: string): void {
  const storage = readStorage();
  if (!storage) return;
  try {
    storage.removeItem(storageKey(tenantSlug));
  } catch {
    /* swallow */
  }
}
